const AUTOCOMPLETE_TRACKER_ID = "749111-1060005";
const RESULTS_API_ENDPOINT = "https://live.luigisbox.com/autocomplete/v2";
/** Focus / empty query: autocomplete v2 rejects bare `q=` — use Top Items API. @see https://docs.luigisbox.com/autocomplete/api/v1/top-items/ */
const TOP_ITEMS_API_ENDPOINT = "https://live.luigisbox.com/v1/top_items";
// Omit `hit_fields`: autocomplete defaults differ from Search; a narrow projection can drop fields we need.
// Storefront path: `old_identity` → `original_url` → `url_en`, then `url` / `uri` (skip GA catalog strings).
const MOBILE_BREAKPOINT = 720;
const LB_RECENT_SEARCHES_KEY = "lb-header-recent-searches-v1";
const LB_RECENT_MAX = 8;

/** Per `.lb-live-search` scope: stale async completions must not reopen the flydown after dismiss. Header: `lbCloseFlydownInScope` does not bump (avoids desktop regressions); explicit dismiss (X, `closeHeaderSearchOverlay`) bumps. */
const lbAutocompleteScopeEpoch = new WeakMap();

function lbNextAutocompleteScopeEpoch(scope) {
    const n = (lbAutocompleteScopeEpoch.get(scope) || 0) + 1;
    lbAutocompleteScopeEpoch.set(scope, n);
    return n;
}

function lbAutocompleteEpochIsStale(scope, opEpoch) {
    return (lbAutocompleteScopeEpoch.get(scope) || 0) !== opEpoch;
}

/** Theme `LiveSearch` + `SearchForm` stay off for Luigi; restore mobile search icon when overlay closes. */
function lbDispatchLuigiHeaderSearchClosed() {
    document.dispatchEvent(new CustomEvent("lb:luigi-header-search-closed"));
}

/** LBX / mobile IME often set `height: 10px; max-height: 0` on `body` and re-apply after our overlay teardown. */
function lbBodyStyleAttrLooksLikeScrollLockLeak(attr) {
    if (!attr || !String(attr).trim()) return false;
    return (
        /\bmax-height\s*:\s*0(px)?\b/i.test(attr) ||
        (/\bheight\s*:\s*10px\b/i.test(attr) && /\bmax-height\s*:\s*0/i.test(attr))
    );
}

function lbStripLeakedBodyLockDimensions() {
    const b = document.body;
    if (!b || !b.style) return;
    b.style.removeProperty("height");
    b.style.removeProperty("max-height");
    b.style.removeProperty("min-height");
}

/** LBX applies body lock on a later frame / after keyboard — clear a few times. */
function lbStripLeakedBodyLockDimensionsBurst() {
    lbStripLeakedBodyLockDimensions();
    requestAnimationFrame(() => {
        lbStripLeakedBodyLockDimensions();
        requestAnimationFrame(lbStripLeakedBodyLockDimensions);
    });
    [50, 200, 500, 1200].forEach((ms) => {
        setTimeout(lbStripLeakedBodyLockDimensions, ms);
    });
}

function lbInstallBodyScrollLockLeakGuard() {
    if (typeof MutationObserver === "undefined" || !document.body) return;
    if (document.body.dataset.lbBodyStyleLeakGuard) return;
    document.body.dataset.lbBodyStyleLeakGuard = "1";
    const mo = new MutationObserver(() => {
        const attr = document.body.getAttribute("style");
        if (lbBodyStyleAttrLooksLikeScrollLockLeak(attr)) lbStripLeakedBodyLockDimensions();
    });
    mo.observe(document.body, { attributes: true, attributeFilter: ["style"] });
}

/** Programmatic focus (e.g. mobile header open): default browser behavior scrolls the page to the input. */
function lbFocusSearchInputWithoutDocumentScroll(el) {
    if (!el || typeof el.focus !== "function") return;
    const sx = window.scrollX;
    const sy = window.scrollY;
    try {
        el.focus({ preventScroll: true });
    } catch {
        el.focus();
    }
    const restore = () => {
        if (window.scrollX !== sx || window.scrollY !== sy) {
            window.scrollTo(sx, sy);
        }
    };
    requestAnimationFrame(restore);
    requestAnimationFrame(restore);
}

const ARROW_ICON = `
    <span class="icon" aria-hidden="true">
        <svg width="13" height="10" viewBox="0 0 13 10" fill="none" xmlns="http://www.w3.org/2000/svg">
            <g clip-path="url(#clip0_28_1588)">
                <path d="M0.9375 5.44336H12.0611M12.0611 5.44336L8.0611 9.44336M12.0611 5.44336L8.0611 1.44336" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
            </g>
            <defs>
                <clipPath id="clip0_28_1588">
                <rect width="13" height="10" fill="white"/>
                </clipPath>
            </defs>
        </svg>
    </span>
`;

function getThemeStrings() {
    return (window.theme && window.theme.strings) || {};
}

/** Header live-search wrapper (mobile overlay + flydown close target only that shell). */
function lbAutocompleteHeaderScope() {
    return (
        document.querySelector("[data-site-header] .lb-live-search") ||
        document.querySelector("header .lb-live-search") ||
        document.querySelector(".site-header .lb-live-search")
    );
}

/** Fresh refs under one `.lb-live-search` — never replace `[data-lb-search-results]` innerHTML. */
function getLbFlydownEls(scope) {
    if (!scope || !(scope instanceof Element)) return null;
    const flydown = scope.querySelector("[data-lb-search-flydown]");
    if (!flydown) return null;
    return {
        flydown,
        inner: flydown.querySelector("[data-lb-search-results]"),
        categories: flydown.querySelector("[data-lb-categories]"),
        products: flydown.querySelector("[data-lb-products]"),
        noResults: flydown.querySelector("[data-lb-no-results]"),
        recentWrap: flydown.querySelector("[data-lb-recent-wrap]"),
        recentList: flydown.querySelector("[data-lb-recent-list]"),
        suggestionsTitle: flydown.querySelector("[data-lb-flydown-suggestions-heading]"),
        productsTitle: flydown.querySelector("[data-lb-flydown-products-heading]"),
        queriesColumn: flydown.querySelector(".lb-search-queries"),
        productsColumn: flydown.querySelector(".lb-search-products"),
    };
}

/** Hide group headings + empty side columns (only show status / recent / real hits). */
function syncLbFlydownGroupTitles(els) {
    if (!els) return;
    const hasSuggestions = !!(els.categories && els.categories.children.length > 0);
    const hasProducts = !!(els.products && els.products.children.length > 0);
    if (els.suggestionsTitle) els.suggestionsTitle.hidden = !hasSuggestions;
    if (els.productsTitle) els.productsTitle.hidden = !hasProducts;

    const hasRecent = !!(els.recentWrap && !els.recentWrap.hidden);
    const showQueriesCol = hasRecent || hasSuggestions;
    const showProductsCol = hasProducts;
    if (els.queriesColumn) els.queriesColumn.hidden = !showQueriesCol;
    if (els.productsColumn) els.productsColumn.hidden = !showProductsCol;

    const flydownInner = els.flydown && els.flydown.querySelector(".search-flydown-inner");
    if (flydownInner) {
        flydownInner.toggleAttribute("data-lb-empty-cols", !showQueriesCol && !showProductsCol);
    }
}

function setFlydownOpen(els, open) {
    if (!els || !els.flydown || !els.inner) return;
    els.flydown.setAttribute("data-animation-state", open ? "open" : "closed");
    els.inner.classList.toggle("visible", open);
    els.inner.setAttribute("data-animation-state", open ? "visible" : "closed");
    const scope = els.flydown.closest(".lb-live-search");
    if (!scope || lbScopeIsHeaderShell(scope) || lbScopeIsSearchResultsPage(scope)) return;
    lbSyncNonHeaderLuigiCancelVisibility(scope);
    requestAnimationFrame(() => {
        requestAnimationFrame(() => lbSyncNonHeaderLuigiCancelVisibility(scope));
    });
}

function escapeHtmlText(s) {
    return String(s)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

/** Scalar feed values: string, array, or `{ url }` (non-image objects → null). */
function lbAcScalar(val) {
    if (val == null || val === "") return null;
    if (Array.isArray(val)) return lbAcScalar(val[0]);
    if (typeof val === "object" && val.url) return lbAcScalar(val.url);
    if (typeof val === "object") return null;
    const out = String(val).trim();
    return out || null;
}

const __lbAcShopifyProductByPath = new Map();

/** `featured_image` / first `images[]` from Shopify JSON cached by `lbAutocompleteFetchShopifyProductByPath`. */
function lbAutocompleteThumbFromCachedProduct(pathKey) {
    if (!pathKey || !__lbAcShopifyProductByPath.has(pathKey)) return null;
    const p = __lbAcShopifyProductByPath.get(pathKey)?.product;
    if (!p) return null;
    const fi = p.featured_image;
    if (typeof fi === "string" && fi.trim()) return fi.trim();
    if (fi && typeof fi === "object" && fi.src) return String(fi.src).trim() || null;
    const z = Array.isArray(p.images) && p.images[0];
    if (typeof z === "string" && z.trim()) return z.trim();
    if (z && typeof z === "object" && z.src) return String(z.src).trim() || null;
    return null;
}

/** Luigi may omit images; prefetch fills `__lbAcShopifyProductByPath` from `old_identity` → `/products/*.js`. */
function lbAutocompleteImageSrcFromHit(hit) {
    const a = (hit && hit.attributes) || {};
    const fromFeed =
        lbAcScalar(a.image_link) ||
        lbAcScalar(a.image) ||
        lbAcScalar(a.thumbnail) ||
        lbAcScalar(a.thumbnail_url);
    if (fromFeed) return fromFeed;
    const key = lbAutocompleteNormalizeProductPath(lbAutocompleteHitProductPath(hit));
    return lbAutocompleteThumbFromCachedProduct(key) || null;
}

const LB_AUTOCOMPLETE_PRICE_KEYS = [
    "price_amount",
    "price",
    "sale_price",
    "price_min",
    "min_price",
];

function lbAutocompletePriceAmount(attrs) {
    if (!attrs) return null;
    for (const key of LB_AUTOCOMPLETE_PRICE_KEYS) {
        const raw = lbAcScalar(attrs[key]);
        if (raw == null || raw === "") continue;
        const n = Number(raw);
        if (!Number.isNaN(n)) return n;
    }
    return null;
}

function lbAutocompletePriceHtml(attrs) {
    const amount = lbAutocompletePriceAmount(attrs);
    if (amount == null) return "";
    const fromLabel = (window.theme && window.theme.strings && window.theme.strings.lbFromLabel) || "From";
    const fromPrefix = `<span class="lb-result-item-price-prefix">${escapeHtmlText(fromLabel)} </span>`;
    return `<span class="lb-result-item-price">${fromPrefix}<span class="money">£${amount.toFixed(2)}</span></span>`;
}

function lbAutocompleteNormalizeProductPath(pathOrUrl) {
    if (pathOrUrl == null || pathOrUrl === "") return null;
    const s = String(pathOrUrl).trim();
    if (!s) return null;
    try {
        const pathOnly = /^https?:/i.test(s) ? new URL(s).pathname : s.split(/[?#]/)[0];
        const basePath = pathOnly.replace(/\/+$/, "");
        if (!/\/products\//i.test(basePath)) return null;
        const slugMatch = basePath.match(/\/products\/([^/?#]+)/i);
        const slug = slugMatch ? slugMatch[1] : "";
        if (
            window.theme &&
            typeof window.theme.lbIsGaCatalogProductSlug === "function" &&
            window.theme.lbIsGaCatalogProductSlug(slug)
        ) {
            return null;
        }
        return basePath;
    } catch {
        return null;
    }
}

function lbIsGaCatalogIdString(str) {
    if (window.theme && typeof window.theme.lbIsGaCatalogIdString === "function") {
        return window.theme.lbIsGaCatalogIdString(str);
    }
    if (str == null || str === "") return false;
    const s = String(str).trim();
    if (/^shopify_[A-Za-z]{2}_\d+_\d+$/i.test(s) || /^\/shopify_[A-Za-z]{2}_\d+_\d+$/i.test(s)) return true;
    try {
        if (/^https?:\/\//i.test(s)) {
            const p = new URL(s).pathname.replace(/\/+$/, "") || "/";
            if (/^\/shopify_[A-Za-z]{2}_\d+_\d+$/i.test(p)) return true;
            if (/\/products\/shopify_[A-Za-z]{2}_\d+_\d+$/i.test(p)) return true;
        }
    } catch {
        /* ignore */
    }
    return false;
}

/** First `/products/...` path from `old_identity` / `original_url` / `url_en` (hit + attributes; arrays ok). */
function lbAutocompletePathFromIdentityFields(hit) {
    if (!hit) return null;
    const a = hit.attributes || {};
    const rawCands = [
        lbAcScalar(hit.old_identity) || lbAcScalar(a.old_identity),
        lbAcScalar(hit.original_url) || lbAcScalar(a.original_url),
        lbAcScalar(hit.url_en) || lbAcScalar(a.url_en),
    ];
    for (let i = 0; i < rawCands.length; i++) {
        const raw = rawCands[i];
        if (!raw) continue;
        let s = String(raw).trim();
        if (!s) continue;
        if (!s.startsWith("/") && !/^https?:/i.test(s)) s = `/${s}`;
        const p = lbAutocompleteNormalizeProductPath(s);
        if (p) return p;
    }
    return null;
}

/** `/products/...` path — identity fields first, then `url` / `uri` (skip GA id strings). */
function lbAutocompleteHitProductPath(hit) {
    if (!hit) return null;
    const fromIdentity = lbAutocompletePathFromIdentityFields(hit);
    if (fromIdentity) return fromIdentity;

    const a = hit.attributes || {};
    const candidates = [];
    const push = (v) => {
        const x = lbAcScalar(v);
        if (x == null || x === "") return;
        const s = String(x).trim();
        if (!s || lbIsGaCatalogIdString(s)) return;
        candidates.push(s);
    };
    push(a.url);
    push(a.uri);
    push(hit.url);

    for (let i = 0; i < candidates.length; i++) {
        let raw = candidates[i];
        if (!/^https?:\/\//i.test(raw) && !raw.startsWith("/") && /products\//i.test(raw)) {
            raw = `/${raw}`;
        }
        let p = lbAutocompleteNormalizeProductPath(raw);
        if (p) return p;
        const s = String(raw).trim();
        if (!/\/products\//i.test(s)) continue;
        try {
            p = lbAutocompleteNormalizeProductPath(new URL(s, "https://example.com").pathname);
            if (p) return p;
        } catch {
            /* continue */
        }
        p = lbAutocompleteNormalizeProductPath(s.split(/[?#]/)[0]);
        if (p) return p;
    }
    return hit._lbStorefrontPath || null;
}

/** `/collections/...` path for category rows (reject GA id strings). */
function lbAutocompleteHitCollectionPath(hit) {
    if (!hit) return null;
    const a = hit.attributes || {};
    const candidates = [];
    const push = (v) => {
        const x = lbAcScalar(v);
        if (x == null || x === "") return;
        const s = String(x).trim();
        if (!s || lbIsGaCatalogIdString(s)) return;
        candidates.push(s);
    };
    push(a.url);
    push(a.uri);
    push(hit.url);
    for (let i = 0; i < candidates.length; i++) {
        let raw = candidates[i];
        if (!/^https?:\/\//i.test(raw) && !raw.startsWith("/") && /collections\//i.test(raw)) {
            raw = `/${raw}`;
        }
        try {
            const pathOnly = /^https?:/i.test(raw) ? new URL(raw).pathname : raw.split(/[?#]/)[0];
            const base = pathOnly.replace(/\/+$/, "");
            if (!/\/collections\//i.test(base)) continue;
            return base.startsWith("/") ? base : `/${base}`;
        } catch {
            /* continue */
        }
    }
    return null;
}

function lbAutocompleteAbsUrlFromPath(path) {
    const shop = window.Shopify && window.Shopify.shop;
    if (!path || !shop) return "#";
    const p = path.startsWith("/") ? path : `/${path}`;
    return `https://${shop}${p}`;
}

/** Full product URL — canonical `/products/{handle}` only (no `?variant=`). */
function lbAutocompleteProductHrefFromHit(hit) {
    const path = lbAutocompleteHitProductPath(hit);
    if (!path) return "#";
    return lbAutocompleteAbsUrlFromPath(path);
}

function lbAutocompleteShopifySelectedOrFirstVariant(product) {
    const vs = product?.variants;
    if (!vs || !vs.length) return null;
    const avail = vs.find((v) => v.available !== false);
    return avail || vs[0];
}

async function lbAutocompleteFetchShopifyProductByPath(pathOrUrl) {
    const basePath = lbAutocompleteNormalizeProductPath(pathOrUrl);
    if (!basePath) return null;
    if (__lbAcShopifyProductByPath.has(basePath)) {
        return __lbAcShopifyProductByPath.get(basePath);
    }
    const shop = window.Shopify && window.Shopify.shop;
    if (!shop) return null;
    try {
        const res = await fetch(`https://${shop}${basePath}.js`);
        if (!res.ok) return null;
        const product = await res.json();
        const entry = { id: product?.id ?? null, product };
        __lbAcShopifyProductByPath.set(basePath, entry);
        return entry;
    } catch {
        return null;
    }
}

function lbAutocompleteItemIdFromFeed(hit) {
    if (
        !hit ||
        !window.theme ||
        typeof window.theme.lbGaItemIdFromLuigiHit !== "function" ||
        typeof window.theme.lbIsCanonicalGaItemId !== "function"
    ) {
        return "";
    }
    const id = window.theme.lbGaItemIdFromLuigiHit(hit);
    return window.theme.lbIsCanonicalGaItemId(id) ? id : "";
}

/** Resolve canonical `shopify_CC_<product>_<variant>` — feed first, then `/products/{handle}.js`. */
async function lbAutocompleteItemId(hit) {
    if (!hit) return "";
    if (hit._lbGaItemId != null) return hit._lbGaItemId;

    const fromFeed = lbAutocompleteItemIdFromFeed(hit);
    if (fromFeed) {
        hit._lbGaItemId = fromFeed;
        return fromFeed;
    }

    if (hit.type !== "product" && hit.type !== "item") {
        hit._lbGaItemId = "";
        return "";
    }

    const path = lbAutocompleteHitProductPath(hit);
    if (!path) {
        hit._lbGaItemId = "";
        return "";
    }

    const ent = await lbAutocompleteFetchShopifyProductByPath(path);
    if (!ent?.product?.id || !window.theme || typeof window.theme.gaCanonicalItemId !== "function") {
        hit._lbGaItemId = "";
        return "";
    }
    hit._lbStorefrontPath = path;
    const variant = lbAutocompleteShopifySelectedOrFirstVariant(ent.product);
    const id = window.theme.gaCanonicalItemId(ent.product.id, variant?.id);
    hit._lbGaItemId =
        id && typeof window.theme.lbIsCanonicalGaItemId === "function" && window.theme.lbIsCanonicalGaItemId(id)
            ? id
            : "";
    return hit._lbGaItemId;
}

async function prefetchLbAutocompleteItemIds(hits) {
    await Promise.all(
        (hits || []).map(async (hit) => {
            await lbAutocompleteItemId(hit);
            if (hit.type !== "product" && hit.type !== "item") return;
            const path = lbAutocompleteHitProductPath(hit);
            if (!path) return;
            await lbAutocompleteFetchShopifyProductByPath(path);
        })
    );
}

/** Top Items DataLayer `item_id` (Luigi “Recommendation”); @see https://docs.luigisbox.com/tutorials/autocomplete/#fire-datalayer-event */
function lbAutocompleteTopItemsDlItemId(hit) {
    if (!hit) return "";
    const t = hit.type;
    const a = hit.attributes || {};

    if (t === "category") {
        const col = lbAutocompleteHitCollectionPath(hit);
        if (col) return col;
        const web = lbAcScalar(a.web_url);
        if (web) {
            try {
                const path = new URL(web, "https://example.com").pathname.replace(/\/+$/, "") || "";
                if (path) return path.startsWith("/") ? path : `/${path}`;
            } catch {
                /* ignore */
            }
        }
        const raw = lbAcScalar(a.url) || lbAcScalar(hit.url);
        if (raw && !lbIsGaCatalogIdString(raw)) {
            const pathOnly = /^https?:/i.test(raw) ? new URL(raw).pathname : String(raw).split(/[?#]/)[0];
            const base = pathOnly.replace(/\/+$/, "");
            if (base && /\/collections\//i.test(base)) return base.startsWith("/") ? base : `/${base}`;
        }
        return "";
    }

    const fromGa = hit._lbGaItemId || lbAutocompleteItemIdFromFeed(hit);
    if (fromGa && window.theme && typeof window.theme.lbIsCanonicalGaItemId === "function" && window.theme.lbIsCanonicalGaItemId(fromGa)) {
        return fromGa;
    }

    const path = lbAutocompleteHitProductPath(hit);
    if (path) return path;

    const raw = lbAcScalar(hit.url) || lbAcScalar(a.url);
    if (raw && !lbIsGaCatalogIdString(raw)) {
        const trimmed = String(raw).trim();
        if (trimmed) return trimmed;
    }
    return "";
}

/** One GA4 ecommerce `items[]` row — all Luigi autocomplete / top_items hit types (product, category, item, …). */
function lbAutocompleteViewItemListRow(hit, index) {
    const item_id = lbAutocompleteTopItemsDlItemId(hit);
    if (!item_id) return null;
    const attrs = hit.attributes || {};
    const item_name = lbAutocompletePlainTitle(attrs);
    const priceVal = lbAutocompletePriceAmount(attrs);
    const apiType = String(hit.type || "").trim() || "product";
    const row = {
        item_id,
        item_name,
        index: index + 1,
        type: apiType,
    };
    if ((apiType === "product" || apiType === "item") && priceVal != null && !Number.isNaN(priceVal)) {
        row.price = priceVal;
    }
    return row;
}

/** Highlight markup in `title` (`<em>`) — strip for alt/visible text. */
function lbAutocompletePlainTitle(attrs) {
    const a = attrs || {};
    let t = lbAcScalar(a.title_common) || lbAcScalar(a.title) || "";
    if (t.indexOf("<") !== -1) {
        t = t.replace(/<[^>]*>/g, "");
    }
    return t.trim();
}

function searchUrlForQuery(q) {
    const base = (window.Theme && window.Theme.routes && window.Theme.routes.search_url) || "/search";
    const sep = base.indexOf("?") === -1 ? "?" : "&";
    return `${base}${sep}q=${encodeURIComponent(q)}`;
}

function readRecentSearches() {
    try {
        const raw = localStorage.getItem(LB_RECENT_SEARCHES_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed)
            ? parsed.filter((t) => typeof t === "string" && t.trim().length > 0)
            : [];
    } catch {
        return [];
    }
}

function saveRecentSearch(term) {
    const t = String(term || "").trim();
    if (t.length < 2 || t.length > 120) return;
    let list = readRecentSearches().filter((x) => x.toLowerCase() !== t.toLowerCase());
    list.unshift(t);
    list = list.slice(0, LB_RECENT_MAX);
    try {
        localStorage.setItem(LB_RECENT_SEARCHES_KEY, JSON.stringify(list));
    } catch {
        /* quota / private mode */
    }
}

function renderRecentIntoFlydown(els) {
    if (!els || !els.recentList || !els.recentWrap) return;
    const list = readRecentSearches();
    els.recentList.innerHTML = list
        .map((term) => {
            const safe = escapeHtmlText(term);
            const enc = encodeURIComponent(term);
            return `
            <li class="lb-result-item lb-result-query lb-result-recent">
                <a href="${searchUrlForQuery(term)}" data-lb-recent-search="${enc}" aria-label="${safe}">
                    <span>${safe}</span>
                    ${ARROW_ICON}
                </a>
            </li>`;
        })
        .join("");
    els.recentWrap.hidden = list.length === 0;
}

// Debounce function to limit the rate of API calls
const debounce = (func, delay) => {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), delay);
    };
};

/** Empty query: show recent (localStorage) + optional Luigi trending; native LiveSearch used Shopify predictive — Luigi path never did. */
async function showEmptyQueryFlydown(scope, opEpoch) {
    const els = getLbFlydownEls(scope);
    if (!els) return;

    if (els.noResults) {
        els.noResults.hidden = true;
        els.noResults.textContent = "";
    }

    renderRecentIntoFlydown(els);
    syncLbFlydownGroupTitles(els);

    let gotHits = false;
    try {
        const params = new URLSearchParams({
            tracker_id: AUTOCOMPLETE_TRACKER_ID,
            type: "product:4,category:6",
        });
        const response = await fetch(`${TOP_ITEMS_API_ENDPOINT}?${params}`);
        if (response.ok) {
            const data = await response.json();
            if (data && data.hits && data.hits.length) {
                await prefetchLbAutocompleteItemIds(data.hits);
                if (lbAutocompleteEpochIsStale(scope, opEpoch)) return;
                renderAutocompleteResults(data, scope);
                topItemsResultsTracking(data);
                gotHits = true;
            }
        }
    } catch {
        /* Top items or network failure — fall through */
    }

    if (lbAutocompleteEpochIsStale(scope, opEpoch)) return;

    if (!gotHits) {
        if (els.categories) els.categories.innerHTML = "";
        if (els.products) els.products.innerHTML = "";
        syncLbFlydownGroupTitles(els);
        const str = getThemeStrings();
        const hasRecent = els.recentWrap && !els.recentWrap.hidden;
        if (!hasRecent && els.noResults) {
            els.noResults.textContent = str.lbTypeToSearch || "Start typing to search";
            els.noResults.hidden = false;
        }
        setFlydownOpen(els, true);
    }
}

// Fetch autocomplete suggestions from Luigi's Box API
async function getLBSuggestions(query, scope) {
    const els = getLbFlydownEls(scope);
    if (!els) return;

    const opEpoch = lbNextAutocompleteScopeEpoch(scope);
    const q = (query || "").trim();
    if (!q) {
        await showEmptyQueryFlydown(scope, opEpoch);
        return;
    }

    const params = new URLSearchParams({
        tracker_id: AUTOCOMPLETE_TRACKER_ID,
        type: "product:4,category:6",
        q,
    });

    try {
        const response = await fetch(`${RESULTS_API_ENDPOINT}?${params}`);
        const data = await response.json();
        if (lbAutocompleteEpochIsStale(scope, opEpoch)) return;
        await prefetchLbAutocompleteItemIds(data.hits);
        if (lbAutocompleteEpochIsStale(scope, opEpoch)) return;
        renderAutocompleteResults(data, scope);
        resultsTracking(data, q);
    } catch (err) {
        console.error("Failed to fetch autocomplete suggestions:", err);
    }
}

// Render autocomplete results in the flydown (`prefetchLbAutocompleteItemIds` should run first).
function renderAutocompleteResults(resultsData, scope) {
    const els = getLbFlydownEls(scope);
    if (!els || !els.categories || !els.products) return;

    if (els.recentWrap) els.recentWrap.hidden = true;
    if (els.noResults) {
        els.noResults.hidden = true;
        els.noResults.textContent = "";
    }

    const hits = resultsData.hits || [];

    if (!hits.length) {
        els.categories.innerHTML = "";
        els.products.innerHTML = "";
        syncLbFlydownGroupTitles(els);
        if (els.noResults) {
            els.noResults.textContent = getThemeStrings().lbNoResults || "No results";
            els.noResults.hidden = false;
        }
        setFlydownOpen(els, true);
        return;
    }

    const categories = [];
    const products = [];

    for (const hit of hits) {
        if (hit.type === "category") categories.push(hit);
        else if (hit.type === "product" || hit.type === "item") products.push(hit);
    }

    els.categories.innerHTML = categories
        .map((hit) => {
            const a = hit.attributes || {};
            const title = lbAutocompletePlainTitle(a);
            const categoryUrl = lbAutocompleteAbsUrlFromPath(lbAutocompleteHitCollectionPath(hit));

            return `
            <li class="lb-result-item lb-result-query">
                <a href="${categoryUrl}" aria-label="${escapeHtmlText(title)}">
                    <span>${escapeHtmlText(title)}</span>
                    ${ARROW_ICON}
                </a>
            </li>
        `;
        })
        .join("");

    els.products.innerHTML = products
        .map((hit) => {
            const a = hit.attributes || {};
            const title = lbAutocompletePlainTitle(a);
            const imgSrc = lbAutocompleteImageSrcFromHit(hit);
            const priceHtml = lbAutocompletePriceHtml(a);
            const trackingItemId = hit._lbGaItemId || lbAutocompleteItemIdFromFeed(hit);
            const img = imgSrc
                ? `<img src="${escapeHtmlText(imgSrc)}" alt="${escapeHtmlText(title)}" data-rimg="noscript" loading="lazy" decoding="async" onerror="this.style.visibility='hidden'">`
                : `<div style="height:140px;background:#f0f0f0" aria-hidden="true"></div>`;
            let productUrl = lbAutocompleteProductHrefFromHit(hit);
            if (productUrl === "#" && window.theme && typeof window.theme.lbVariantStorefrontUrlFromGaItemId === "function") {
                const v = window.theme.lbVariantStorefrontUrlFromGaItemId(trackingItemId);
                if (v) productUrl = v;
            }

            return `
            <li class="lb-result-item lb-result-product"${trackingItemId ? ` data-item-id="${escapeHtmlText(trackingItemId)}"` : ""}>
                <a href="${productUrl}" aria-label="${escapeHtmlText(title)}">
                    <figure class="lb-result-product-image">
                        ${img}
                    </figure>
                    <span class="lb-result-product-meta">
                        <span class="lb-result-item-title">${escapeHtmlText(title)}</span>
                        ${priceHtml}
                    </span>
                    ${ARROW_ICON}
                </a>
            </li>
        `;
        })
        .join("");

    syncLbFlydownGroupTitles(els);
    setFlydownOpen(els, true);
}

/** Luigi Top Items → `view_item_list` “Recommendation” + recommender filters. */
function topItemsResultsTracking(data) {
    const hits = data.hits || [];
    const items = hits.map((hit, i) => lbAutocompleteViewItemListRow(hit, i)).filter(Boolean);

    if (!items.length) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: "view_item_list",
        ecommerce: {
            item_list_name: "Recommendation",
            items,
            filters: {
                Recommender: "autocomplete_popup",
                RecommenderClientId: "autocomplete_popup",
            },
        },
    });
}

// Track autocomplete results and send data to Google Analytics via dataLayer
function resultsTracking(data, query) {
    const hits = data.hits || [];
    const items = hits.map((hit, i) => lbAutocompleteViewItemListRow(hit, i)).filter(Boolean);

    if (!items.length) return;

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
        event: "view_item_list",
        ecommerce: {
            item_list_name: "Autocomplete",
            search_term: query,
            items,
        },
    });
}

document.querySelectorAll("[data-lb-search-flydown]").forEach((flydownRoot) => {
    flydownRoot.addEventListener("click", (e) => {
        const scope = flydownRoot.closest(".lb-live-search");
        const resultItem = e.target.closest(".lb-result-item[data-item-id]");
        const itemId = resultItem && resultItem.dataset.itemId;
        if (
            itemId &&
            window.theme &&
            typeof window.theme.lbIsCanonicalGaItemId === "function" &&
            window.theme.lbIsCanonicalGaItemId(itemId)
        ) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: "select_item",
                ecommerce: {
                    items: [{ item_id: itemId }],
                },
            });
        }

        const recentA = e.target.closest("a[data-lb-recent-search]");
        const input = scope && scope.querySelector("[data-lb-search-input]");
        if (recentA && input && !e.metaKey && !e.ctrlKey && !e.shiftKey && e.button === 0) {
            e.preventDefault();
            const enc = recentA.getAttribute("data-lb-recent-search");
            const term = enc ? decodeURIComponent(enc) : "";
            if (!term) return;
            input.value = term;
            void getLBSuggestions(term, scope);
        }
    });
});

function lbScopeIsHeaderShell(scope) {
    if (!scope) return false;
    if (scope.closest(".image-with-search-overlay-text-wrapper")) return false;
    const designated = lbAutocompleteHeaderScope();
    if (designated && designated === scope) return true;
    return !!scope.closest("header, [data-site-header], .site-header");
}

/** Luigi search results section (`sections/lb-search-results`): in-page search, not header. */
function lbScopeIsSearchResultsPage(scope) {
    return !!(scope && scope.closest && scope.closest("#lb-layout[data-lb-search]"));
}

/**
 * Image banner / search-section use the same `[data-lb-search-flydown-close]` as the header, but they are not
 * `lbScopeIsHeaderShell` — overlay + focus logic never toggled their cancel (only `document.querySelector` = first match).
 */
function lbSyncNonHeaderLuigiCancelVisibility(scope) {
    if (!scope || lbScopeIsHeaderShell(scope) || lbScopeIsSearchResultsPage(scope)) return;
    const cancel = scope.querySelector("[data-lb-search-flydown-close]");
    if (!cancel) return;
    const input = scope.querySelector("[data-lb-search-input]");
    const form = scope.querySelector("[data-lb-search-form]");
    if (!input || !form) return;
    const els = getLbFlydownEls(scope);
    const flydownOpen = !!(els && els.inner && els.inner.classList.contains("visible"));
    const ae = document.activeElement;
    /* Empty + desktop: mousedown on X focuses the button before `click`; if we only keyed off `ae === input`, sync hid the X (`visibility:hidden`) and the click never fired. Keep visible while the cancel (or its subtree) is focused. */
    const inputFocused = ae === input;
    const cancelFocused = !!(cancel && ae && (cancel === ae || cancel.contains(ae)));
    const hasText = !!String(input.value || "").trim();
    /* Tie the X to the flydown: with it closed, never show just because `q` is filled (that left `style.visibility:visible` stuck). While open: same as before + cancel focus for reliable click. */
    const showCancel = flydownOpen
        ? cancelFocused || hasText || inputFocused
        : cancelFocused;
    cancel.style.visibility = showCancel ? "visible" : "hidden";
}

/** Theme hides header `.live-search` until `live-search--takeover` (empire `LiveSearch`); Luigi uses a stub — mirror that when opening from the mobile icon. */
function lbHeaderMobileSearchIconMode(scope) {
    return !!(scope && scope.closest("[data-site-header-mobile-search-button]"));
}

function lbEnsureLuigiHeaderMobileTakeover(scope) {
    if (!scope || !lbScopeIsHeaderShell(scope)) return;
    if (window.innerWidth >= MOBILE_BREAKPOINT) return;
    if (!lbHeaderMobileSearchIconMode(scope)) return;
    scope.classList.add("live-search--takeover");
    scope.setAttribute("data-animation-state", "open");
    document.body.classList.add("mobile-search-takeover-active");
}

function lbStripLuigiHeaderMobileTakeover(scope) {
    if (!scope || !lbScopeIsHeaderShell(scope)) return;
    scope.classList.remove("live-search--takeover");
    scope.removeAttribute("data-animation-state");
    document.body.classList.remove("mobile-search-takeover-active");
}

/** Which `.lb-live-search` last turned on the global `[data-lb-overlay]` (header desktop + mobile menu only). */
let lbAutocompleteOverlayOwnerScope = null;

/** Search template (`#lb-layout[data-lb-search]`): purple active ring + cancel visibility (no dimmer overlay). */
let lbSearchPageAutocompleteOpenScope = null;

function lbStripSearchPageFlydownChrome(scope) {
    if (!scope) return;
    const cancel = scope.querySelector("[data-lb-search-page-flydown-close]");
    if (cancel) cancel.style.visibility = "hidden";
    const form = scope.querySelector("[data-lb-search-form]");
    if (form) form.classList.remove("active");
    const submit = scope.querySelector("[data-lb-search-page-submit]");
    if (submit) submit.classList.remove("active");
    if (lbSearchPageAutocompleteOpenScope === scope) {
        lbSearchPageAutocompleteOpenScope = null;
    }
}

function lbShowSearchPageFlydownChrome(scope) {
    const form = scope.querySelector("[data-lb-search-form]");
    const cancel = scope.querySelector("[data-lb-search-page-flydown-close]");
    const submit = scope.querySelector("[data-lb-search-page-submit]");
    if (form) form.classList.add("active");
    if (cancel) cancel.style.visibility = "visible";
    if (submit) submit.classList.add("active");
    lbSearchPageAutocompleteOpenScope = scope;
}

function lbBindSearchPageAutocompleteExtras(scope) {
    const layout = scope.closest("#lb-layout[data-lb-search]");
    if (!layout || layout.dataset.lbSearchPageAutocompleteBound) return;
    layout.dataset.lbSearchPageAutocompleteBound = "1";

    const closeTakeover = () => {
        const input = scope.querySelector("[data-lb-search-input]");
        if (input) input.value = "";
        lbCloseFlydownInScope(scope);
        lbStripSearchPageFlydownChrome(scope);
        if (input) input.blur();
    };

    const cancel = scope.querySelector("[data-lb-search-page-flydown-close]");
    if (cancel) {
        cancel.addEventListener("click", (e) => {
            e.preventDefault();
            closeTakeover();
        });
    }
}

function lbStripSearchAutocompleteOverlayChrome() {
    const overlay = document.querySelector("[data-lb-overlay]");
    if (overlay) overlay.style.display = "none";
    const ownerScope = lbAutocompleteOverlayOwnerScope || lbAutocompleteHeaderScope();
    lbAutocompleteOverlayOwnerScope = null;
    const headerCancel = ownerScope && ownerScope.querySelector("[data-lb-search-flydown-close]");
    if (headerCancel) headerCancel.style.visibility = "hidden";
    const headerSubmit = ownerScope && ownerScope.querySelector("[data-lb-search-submit]");
    if (headerSubmit) headerSubmit.classList.remove("active");
    lbStripLeakedBodyLockDimensions();
}

/** @param {{ clearQuery?: boolean }} [opts] — default clears the field; `clearQuery: false` only tears down overlay / active chrome (same as banner: click-out does not wipe the query). */
function lbClearHeaderSearchOverlayState(scope, opts) {
    if (!lbScopeIsHeaderShell(scope)) return;
    const clearQuery = !opts || opts.clearQuery !== false;
    const form = scope && scope.querySelector("[data-lb-search-form]");
    const input = scope && scope.querySelector("[data-lb-search-input]");
    if (form) form.classList.remove("active");
    if (clearQuery && input) input.value = "";
    document.documentElement.classList.remove("sf-search-open");
    lbStripLuigiHeaderMobileTakeover(scope);
}

function lbCloseFlydownInScope(scope) {
    if (!scope) return;
    if (!lbScopeIsHeaderShell(scope)) lbNextAutocompleteScopeEpoch(scope);
    const els = getLbFlydownEls(scope);
    if (els) setFlydownOpen(els, false);
}

/** Header / site-header `.lb-live-search`: each scope has its own X — not only `lbAutocompleteHeaderScope()`’s first cancel (that was the only one wired to the overlay). */
function lbDismissLuigiHeaderShellFromCancel(scope, input, scopedCloseBtn, scopedSubmitBtn) {
    if (!scope || !lbScopeIsHeaderShell(scope)) return;
    if (input) input.value = "";
    if (lbAutocompleteOverlayOwnerScope === scope) {
        closeHeaderSearchOverlay();
    } else {
        lbCloseFlydownInScope(scope);
        lbClearHeaderSearchOverlayState(scope);
        if (scopedCloseBtn) scopedCloseBtn.style.visibility = "hidden";
        if (scopedSubmitBtn) scopedSubmitBtn.classList.remove("active");
        lbDispatchLuigiHeaderSearchClosed();
        const n = (lbAutocompleteScopeEpoch.get(scope) || 0) + 1;
        lbAutocompleteScopeEpoch.set(scope, n);
    }
    if (input) input.blur();
}

function bindLbAutocompleteScope(scope) {
    const input = scope.querySelector("[data-lb-search-input]");
    const form = scope.querySelector("[data-lb-search-form]");
    const flydown = scope.querySelector("[data-lb-search-flydown]");
    if (!input || !form || !flydown) return;

    const isHeaderShell = lbScopeIsHeaderShell(scope);
    const globalOverlay = document.querySelector("[data-lb-overlay]");
    const scopedCloseBtn = scope.querySelector("[data-lb-search-flydown-close]");
    const scopedSubmitBtn = scope.querySelector("[data-lb-search-submit]");

    if (lbScopeIsSearchResultsPage(scope)) {
        lbBindSearchPageAutocompleteExtras(scope);
    }

    /* Image banner: tap-to-focus still scrolls the document on many browsers — restore after focus. */
    if (scope.closest(".image-with-search-overlay-text-wrapper")) {
        let lbHeroSearchPointerFocus = false;
        input.addEventListener("pointerdown", () => {
            lbHeroSearchPointerFocus = true;
        });
        input.addEventListener("blur", () => {
            lbHeroSearchPointerFocus = false;
        });
        input.addEventListener("focus", () => {
            if (!lbHeroSearchPointerFocus) return;
            lbHeroSearchPointerFocus = false;
            const sx = window.scrollX;
            const sy = window.scrollY;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (window.scrollX !== sx || window.scrollY !== sy) {
                        window.scrollTo(sx, sy);
                    }
                });
            });
        });
    }

    input.addEventListener(
        "input",
        debounce((e) => {
            void getLBSuggestions(e.target.value, scope);
        }, 300)
    );

    if (!isHeaderShell && !lbScopeIsSearchResultsPage(scope)) {
        input.addEventListener("input", () => lbSyncNonHeaderLuigiCancelVisibility(scope));
        input.addEventListener("blur", () => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => lbSyncNonHeaderLuigiCancelVisibility(scope));
            });
        });
        if (scopedCloseBtn) {
            scopedCloseBtn.addEventListener("click", (e) => {
                e.preventDefault();
                input.value = "";
                lbCloseFlydownInScope(scope);
                scopedCloseBtn.blur();
                lbSyncNonHeaderLuigiCancelVisibility(scope);
                input.blur();
            });
        }
    }

    if (isHeaderShell && scopedCloseBtn) {
        scopedCloseBtn.addEventListener("click", (e) => {
            e.preventDefault();
            lbDismissLuigiHeaderShellFromCancel(scope, input, scopedCloseBtn, scopedSubmitBtn);
        });
    }

    input.addEventListener("focus", () => {
        document.querySelectorAll(".lb-live-search").forEach((other) => {
            if (other === scope) return;
            const oEls = getLbFlydownEls(other);
            if (oEls && oEls.inner && oEls.inner.classList.contains("visible")) {
                lbCloseFlydownInScope(other);
            }
        });

        if (lbSearchPageAutocompleteOpenScope && lbSearchPageAutocompleteOpenScope !== scope) {
            lbStripSearchPageFlydownChrome(lbSearchPageAutocompleteOpenScope);
        }

        const prevGlobal = lbAutocompleteOverlayOwnerScope;
        if (prevGlobal && prevGlobal !== scope) {
            lbClearHeaderSearchOverlayState(prevGlobal, { clearQuery: false });
            lbStripSearchAutocompleteOverlayChrome();
            lbDispatchLuigiHeaderSearchClosed();
            if (lbScopeIsHeaderShell(prevGlobal)) {
                const n = (lbAutocompleteScopeEpoch.get(prevGlobal) || 0) + 1;
                lbAutocompleteScopeEpoch.set(prevGlobal, n);
            }
        }

        /* Hero / search-section focus: collapse header flydown + overlay chrome without wiping the header query (unique `lb_q_*` names). */
        if (!isHeaderShell && !lbScopeIsSearchResultsPage(scope)) {
            const hs = lbAutocompleteHeaderScope();
            if (hs && lbScopeIsHeaderShell(hs)) {
                const hi = hs.querySelector("[data-lb-search-input]");
                const hels = getLbFlydownEls(hs);
                const headerFlyOpen = !!(hels && hels.inner && hels.inner.classList.contains("visible"));
                const hadText = !!(hi && String(hi.value || "").trim());
                if (hadText || headerFlyOpen || lbAutocompleteOverlayOwnerScope === hs) {
                    if (lbAutocompleteOverlayOwnerScope === hs) {
                        lbStripSearchAutocompleteOverlayChrome();
                    }
                    lbCloseFlydownInScope(hs);
                    lbClearHeaderSearchOverlayState(hs, { clearQuery: false });
                    lbDispatchLuigiHeaderSearchClosed();
                    const n = (lbAutocompleteScopeEpoch.get(hs) || 0) + 1;
                    lbAutocompleteScopeEpoch.set(hs, n);
                }
            }
        }

        if (
            isHeaderShell &&
            window.innerWidth >= MOBILE_BREAKPOINT &&
            globalOverlay &&
            scopedCloseBtn &&
            scopedSubmitBtn
        ) {
            globalOverlay.style.display = "block";
            lbAutocompleteOverlayOwnerScope = scope;
            scopedCloseBtn.style.visibility = "visible";
            form.classList.add("active");
            scopedSubmitBtn.classList.add("active");
        } else if (!isHeaderShell && lbScopeIsSearchResultsPage(scope)) {
            lbShowSearchPageFlydownChrome(scope);
        }

        if (!isHeaderShell && !lbScopeIsSearchResultsPage(scope)) {
            lbSyncNonHeaderLuigiCancelVisibility(scope);
        }

        if (!input.value.trim()) {
            const opEpoch = lbNextAutocompleteScopeEpoch(scope);
            void showEmptyQueryFlydown(scope, opEpoch);
        }
    });

    form.addEventListener("submit", (e) => {
        saveRecentSearch(input.value);
        if (lbScopeIsSearchResultsPage(scope)) {
            lbCloseFlydownInScope(scope);
            lbStripSearchPageFlydownChrome(scope);
            return;
        }
        // Luigi bypasses theme `SearchForm` / `LiveSearch`; implicit GET submit is flaky with overlay
        // + mobile IME — same as explicit navigation to `action` + `q` (and any other named fields).
        const q = input.value.trim();
        if (!q) {
            e.preventDefault();
            return;
        }
        const action = form.getAttribute("action");
        if (!action) return;
        e.preventDefault();
        const url = new URL(action, window.location.origin);
        /* `live-search-form` Luigi inputs use unique `name="lb_q_*"` so UAs do not link fields; Shopify still expects `q`. */
        url.searchParams.set("q", q);
        const queryFieldName = input.name;
        const fd = new FormData(form);
        fd.forEach((val, key) => {
            if (val === "" || val == null) return;
            if (key === "q" || key === queryFieldName) return;
            url.searchParams.set(key, val);
        });
        window.location.assign(url.toString());
    });
}

document.querySelectorAll(".lb-live-search").forEach(bindLbAutocompleteScope);

/** Fired from empire `StaticHeader` when the mobile search icon is tapped (Luigi replaces `LiveSearch.open`). */
function lbOpenLuigiHeaderMobileSearch() {
    if (window.innerWidth >= MOBILE_BREAKPOINT) return;
    if (lbSearchPageAutocompleteOpenScope) {
        lbStripSearchPageFlydownChrome(lbSearchPageAutocompleteOpenScope);
    }
    const globalOverlay = document.querySelector("[data-lb-overlay]");
    const hs = lbAutocompleteHeaderScope();
    const headerCancel = hs && hs.querySelector("[data-lb-search-flydown-close]");
    const headerSubmit = hs && hs.querySelector("[data-lb-search-submit]");
    const headerForm = hs && hs.querySelector("[data-lb-search-form]");
    const headerInput = hs && hs.querySelector("[data-lb-search-input]");
    if (hs) lbEnsureLuigiHeaderMobileTakeover(hs);
    if (globalOverlay) {
        globalOverlay.style.display = "block";
        lbAutocompleteOverlayOwnerScope = hs;
    }
    if (headerCancel) headerCancel.style.visibility = "visible";
    if (headerForm) headerForm.classList.add("active");
    if (headerSubmit) headerSubmit.classList.add("active");
    requestAnimationFrame(() => {
        if (headerInput) lbFocusSearchInputWithoutDocumentScroll(headerInput);
    });
}

document.addEventListener("lb:luigi-header-search-open", () => {
    lbOpenLuigiHeaderMobileSearch();
});

function closeHeaderSearchOverlay() {
    const owner = lbAutocompleteOverlayOwnerScope;
    lbStripSearchAutocompleteOverlayChrome();
    const scopeToClose = owner || lbAutocompleteHeaderScope();
    lbCloseFlydownInScope(scopeToClose);
    lbClearHeaderSearchOverlayState(scopeToClose, { clearQuery: false });
    lbDispatchLuigiHeaderSearchClosed();
    if (scopeToClose && lbScopeIsHeaderShell(scopeToClose)) {
        const n = (lbAutocompleteScopeEpoch.get(scopeToClose) || 0) + 1;
        lbAutocompleteScopeEpoch.set(scopeToClose, n);
    }
}

/**
 * When focus or pointer moves to `t` outside a `.lb-live-search`, dismiss that scope’s flydown
 * (same rules as outside-click). `focusin` on document mirrors nav mega-menu closing on Tab away.
 */
function lbDismissLuigiAutocompleteForOutsideTarget(t) {
    if (!t || !(t instanceof Element)) return;
    if (t.closest("[data-lb-overlay]")) return;

    document.querySelectorAll(".lb-live-search").forEach((scope) => {
        if (scope.contains(t)) return;

        if (lbScopeIsSearchResultsPage(scope)) {
            const els = getLbFlydownEls(scope);
            const flydownOpen = !!(els && els.inner && els.inner.classList.contains("visible"));
            const chromeOn = lbSearchPageAutocompleteOpenScope === scope;
            if (flydownOpen || chromeOn) {
                lbCloseFlydownInScope(scope);
                lbStripSearchPageFlydownChrome(scope);
                const input = scope.querySelector("[data-lb-search-input]");
                if (input) input.blur();
            }
            return;
        }

        const els = getLbFlydownEls(scope);
        if (!els || !els.inner || !els.inner.classList.contains("visible")) return;

        lbCloseFlydownInScope(scope);

        if (lbAutocompleteOverlayOwnerScope === scope) {
            lbStripSearchAutocompleteOverlayChrome();
            lbClearHeaderSearchOverlayState(scope, { clearQuery: false });
            lbDispatchLuigiHeaderSearchClosed();
            if (lbScopeIsHeaderShell(scope)) {
                const n = (lbAutocompleteScopeEpoch.get(scope) || 0) + 1;
                lbAutocompleteScopeEpoch.set(scope, n);
            }
        } else if (lbScopeIsHeaderShell(scope)) {
            lbClearHeaderSearchOverlayState(scope, { clearQuery: false });
            lbDispatchLuigiHeaderSearchClosed();
            const n = (lbAutocompleteScopeEpoch.get(scope) || 0) + 1;
            lbAutocompleteScopeEpoch.set(scope, n);
        }
    });
}

document.addEventListener("pointerdown", (e) => lbDismissLuigiAutocompleteForOutsideTarget(e.target), true);

document.addEventListener("focusin", (e) => lbDismissLuigiAutocompleteForOutsideTarget(e.target), true);

const overlayEl = document.querySelector("[data-lb-overlay]");
if (overlayEl) {
    overlayEl.addEventListener("click", closeHeaderSearchOverlay);
}

(function lbSetupBodyLeakRecovery() {
    if (document.documentElement.dataset.lbBodyLeakRecovery) return;
    document.documentElement.dataset.lbBodyLeakRecovery = "1";
    document.addEventListener("lb:luigi-header-search-closed", lbStripLeakedBodyLockDimensionsBurst);
    const boot = () => {
        if (document.body) lbInstallBodyScrollLockLeakGuard();
    };
    if (document.body) boot();
    else document.addEventListener("DOMContentLoaded", boot, { once: true });
})();
